/**
 * First-run state (design system Components · 09, First run & guidance).
 *
 * What a person has seen is stored per user and per tour version so a tour is
 * never re-shown once seen, skipped or completed, and a future revision of the
 * tour can show again exactly once. The field's rule is server-side
 * persistence; the account profile has no field for it yet, so this lives in
 * the browser (localStorage) with the same shape a profile field would take —
 * moving it is the recorded follow-up (design-system backlog item 16).
 *
 * "Hide for now" on Home's setup banner is a session preference
 * (sessionStorage): it survives navigation, not a new visit.
 */
export const FIRST_RUN_VERSION = 1

export type TourStatus = 'unseen' | 'skipped' | 'completed'

export interface FirstRunState {
  version: number
  /** The four-step coach-mark tour on Home. */
  tour: TourStatus
  /** The "See how Aria answers" sample call has been played. */
  sample: boolean
}

export const FIRST_RUN_CHANGED = 'tenfour:first-run:changed'
/** Instrumentation hook: `detail` is `{ name, userId, ...fields }`. */
export const FIRST_RUN_TRACK = 'tenfour:first-run:track'

const DEFAULT: FirstRunState = { version: FIRST_RUN_VERSION, tour: 'unseen', sample: false }

const stateKey = (userId: string) => `tt_first_run:${userId}`
const hiddenKey = (userId: string) => `tt_first_run_hidden:${userId}`

export function readFirstRun(userId: string): FirstRunState {
  try {
    const raw = localStorage.getItem(stateKey(userId))
    if (!raw) return DEFAULT
    const parsed = JSON.parse(raw) as Partial<FirstRunState>
    // A new tour version starts unseen; older records are ignored, not migrated.
    if (parsed.version !== FIRST_RUN_VERSION) return DEFAULT
    return { ...DEFAULT, ...parsed, version: FIRST_RUN_VERSION }
  } catch {
    return DEFAULT
  }
}

export function writeFirstRun(userId: string, patch: Partial<Omit<FirstRunState, 'version'>>): FirstRunState {
  const next: FirstRunState = { ...readFirstRun(userId), ...patch, version: FIRST_RUN_VERSION }
  try {
    localStorage.setItem(stateKey(userId), JSON.stringify(next))
  } catch {
    // Quota or privacy mode: the state just won't survive a reload.
  }
  window.dispatchEvent(new CustomEvent(FIRST_RUN_CHANGED, { detail: { userId } }))
  return next
}

export function readHiddenForSession(userId: string): boolean {
  try {
    return sessionStorage.getItem(hiddenKey(userId)) === '1'
  } catch {
    return false
  }
}

export function writeHiddenForSession(userId: string, hidden: boolean): void {
  try {
    if (hidden) sessionStorage.setItem(hiddenKey(userId), '1')
    else sessionStorage.removeItem(hiddenKey(userId))
  } catch {
    // ignore
  }
  window.dispatchEvent(new CustomEvent(FIRST_RUN_CHANGED, { detail: { userId } }))
}

/** Emits a measurement event (tour started / step / completed / skipped, sample played…). */
export function trackFirstRun(name: string, detail: Record<string, unknown> = {}): void {
  window.dispatchEvent(new CustomEvent(FIRST_RUN_TRACK, { detail: { name, at: Date.now(), ...detail } }))
}
